import Vue from 'vue';
import VueRouter from 'vue-router';
import MessageDistributionListDetail from '@/views/super-admin/MessageDistributionListDetail.vue';
import RoleMixin from '@/mixins/RoleMixin.vue';
import { parseJwt } from '@/utils';
import { userManager } from '@/utils/auth';
import { ROLES } from '../utils/constants';
import * as Sentry from '@sentry/vue';

Vue.use(VueRouter);

const superRoutes = [
  {
    path: '/org-management',
    name: 'OrgManagement',
    component: () => import('@/views/super-admin/OrgManagement.vue'),
    meta: { title: 'Organization Management - Performa Labs' },
  },
  {
    path: '/org-management/:id',
    name: 'OrgDetail',
    component: () => import('@/views/super-admin/OrgDetail.vue'),
    meta: { title: 'Organization Detail - Performa Labs' },
  },
  {
    path: '/super-admins',
    name: 'SuperAdmins',
    component: () => import('@/views/super-admin/SuperAdmins.vue'),
    meta: { title: 'Super Admins - Performa Labs' },
  },
  {
    path: '/tc-violations',
    name: 'Violations',
    component: () => import('@/views/super-admin/Violations.vue'),
    meta: { title: 'T&C Violations - Performa Labs' },
  },
  {
    path: '/security',
    name: 'Security',
    component: () => import('@/views/super-admin/Security.vue'),
    meta: { title: 'Security Dashboard - Performa Labs' },
  },
  {
    path: '/security/events',
    name: 'SecurityEvents',
    component: () => import('@/views/super-admin/SecurityEventsList.vue'),
    meta: { title: 'Security Dashboard - Performa Labs' },
  },
  {
    path: '/security/blacklist',
    name: 'SecurityBlacklist',
    component: () => import('@/views/super-admin/SecurityBlackList.vue'),
    meta: { title: 'Security Dashboard - Performa Labs' },
  },
  {
    path: '/messages',
    name: 'Messages',
    component: () => import('@/views/super-admin/Messages.vue'),
    meta: { title: 'Messages - Performa Labs' },
  },
  {
    path: '/messages/distribution-list',
    name: 'MessageDistributionList',
    component: () => import('@/views/super-admin/MessageDistributionList.vue'),
    meta: { title: 'Message Distribution List - Performa Labs' },
  },
  {
    path: '/messages/distribution-list/add',
    name: 'MessageDistributionListDetail',
    component: MessageDistributionListDetail,
    meta: { title: 'Add Message Distribution List - Performa Labs' },
  },
  {
    path: '/messages/distribution-list/:id',
    name: 'MessageDistributionListDetailEdit',
    component: MessageDistributionListDetail,
    meta: { title: 'Edit Message Distribution List - Performa Labs' },
  },
  {
    path: '/messages/new',
    name: 'New Message',
    component: () => import('@/views/super-admin/MessageDetails.vue'),
    meta: { title: 'New Message - Performa Labs' },
  },
  {
    path: '/messages/:id',
    name: 'Edit Message',
    component: () => import('@/views/super-admin/MessageDetails.vue'),
    meta: { title: 'Edit Message - Performa Labs' },
  },
  {
    path: '/faq-update',
    name: 'FAQ Update',
    component: () => import('@/views/super-admin/FAQUpdate.vue'),
    meta: { title: 'Edit FAQ Documents - Performa Labs' },
  },
];

const routes = [
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      ...superRoutes,
      {
        path: '/reporting',
        name: 'Reporting',
        component: () => import('@/views/UsageReporting.vue'),
        meta: {
          title: 'Usage Reporting - Performa Labs',
          traineesAllowed: false,
        },
      },
      {
        path: '/policies',
        name: 'Policy',
        component: () => import('@/views/Policy.vue'),
        meta: { title: 'Policy - Performa Labs', traineesAllowed: false },
      },
      {
        path: '/policies/:id',
        name: 'PolicyManagement',
        component: () => import('@/views/PolicyManagement.vue'),
        meta: {
          title: 'Policy Management - Performa Labs',
          traineesAllowed: false,
        },
      },
      {
        path: '/officers',
        redirect: { name: 'UserManagement' },
      },
      {
        path: '/users',
        name: 'UserManagement',
        component: () => import('@/views/UserManagement.vue'),
        meta: {
          title: 'User Management - Performa Labs',
          traineesAllowed: false,
        },
      },
      {
        path: '/officers/:id',
        redirect: { name: 'UserDetail' },
      },
      {
        path: '/users/:id',
        name: 'UserDetail',
        component: () => import('@/views/UserDetail.vue'),
        meta: {
          title: 'User Details - Performa Labs',
          traineesAllowed: false,
        },
      },
      {
        path: '/courses',
        name: 'CourseManagement',
        component: () => import('@/views/CourseManagement.vue'),
        meta: { title: 'Courses - Performa Labs', traineesAllowed: false },
      },
      {
        path: '/downloads',
        name: 'AppDownload',
        component: () => import('@/views/AppDownload.vue'),
        meta: { title: 'App Download - Performa Labs', traineesAllowed: true },
      },
      {
        path: '/downloads/:platform',
        name: 'AppDownloadDetails',
        component: () => import('@/views/AppDownloadDetails.vue'),
        meta: {
          title: 'App Download by Platform - Performa Labs',
          traineesAllowed: true,
        },
      },
      {
        path: '/faq',
        name: 'FAQ',
        component: () => import('@/views/FAQ.vue'),
        meta: { title: 'FAQ - Performa Labs', traineesAllowed: true },
      },
      {
        path: '/help',
        name: 'Help',
        component: () => import('@/views/Help.vue'),
        meta: { title: 'Help - Performa Labs', traineesAllowed: false },
      },
      {
        path: '*',
        redirect: '/reporting',
      },
      // {
      //   path: '/login',
      //   name: 'Login',
      //   // route level code-splitting
      //   // this generates a separate chunk (login.[hash].js) for this route
      //   // which is lazy-loaded when the route is visited.
      //   component: () => import(/* webpackChunkName: "login" */ '../Login.vue'),
      // },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(async (to, from, next) => {
  try {
    const token = (await userManager.getUser()).access_token;
    const parsedToken = parseJwt(token);
    const role = RoleMixin.methods.$getHighestPrivilegedRole(parsedToken.rol);
    if (role === ROLES.USER && !to.meta.traineesAllowed) {
      next({ name: 'AppDownload' });
      return;
    }
  } catch (error) {
    Sentry.withScope(function (scope) {
      scope.setLevel(Sentry.Severity.Fatal);
      Sentry.captureException(error);
    });
  }
  next();
});

router.afterEach((to) => {
  Vue.nextTick(() => {
    document.title = to.meta?.title || 'Performa Labs';
  });
});

export default router;
